import { Component } from '@angular/core';
import { CommonModule } from '@angular/common';
import { BehaviorSubject, Observable, combineLatest, map, startWith, switchMap } from 'rxjs';
import { FormsModule } from '@angular/forms';

import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';

import { AdminWorldsService, AdminWorldSummary } from '../../api/admin-worlds.service';
import {
	AdminWorldUsersService,
	AdminWorldUsersListItem,
	AdminWorldUsersListResponse,
} from '../../api/admin-world-users.service';

@Component({
	selector: 'of-admin-world-users-page',
	standalone: true,
	imports: [
		CommonModule,
		FormsModule,
		MatButtonModule,
		MatCardModule,
		MatProgressBarModule,
		MatFormFieldModule,
		MatInputModule,
		MatSelectModule,
	],
	template: `
		<div class="page">
			<mat-card class="card" *ngIf="vm$ | async as vm; else loading">
				<h2>Admin · World Users</h2>
				<p class="muted">Manage world memberships and per-world roles.</p>

				<div class="actions" style="justify-content: flex-end;">
					<button mat-button (click)="refresh()">Refresh</button>
				</div>

				<div style="display:grid; grid-template-columns: 2fr 1fr; gap: 12px; align-items: end;">
					<mat-form-field appearance="fill" style="width: 100%;">
						<mat-label>World</mat-label>
						<mat-select [(ngModel)]="selectedWorldId" name="selectedWorld" (selectionChange)="refresh()">
							<mat-option *ngFor="let w of vm.worlds" [value]="w.id">
								{{ w.name }} ({{ w.status }})
							</mat-option>
						</mat-select>
					</mat-form-field>
					<div class="muted" style="font-size: 13px;" *ngIf="vm.selectedWorld as w">
						Owner: {{ w.ownerEmail }}
					</div>
				</div>

				<div *ngIf="vm.usersVm as usersVm">
					<div style="padding: 12px 0; border-top: 1px solid rgba(255,255,255,0.08);">
						<div style="font-weight: 600; margin-bottom: 8px;">Add User</div>
						<div style="display:grid; grid-template-columns: 2fr 1fr 1fr; gap: 12px;">
							<mat-form-field appearance="fill" style="width:100%;">
								<mat-label>Email</mat-label>
								<input matInput [(ngModel)]="addEmail" name="addEmail" />
							</mat-form-field>
							<mat-form-field appearance="fill" style="width:100%;">
								<mat-label>Role</mat-label>
								<mat-select [(ngModel)]="addRole" name="addRole">
									<mat-option value="member">member</mat-option>
									<mat-option value="gm">gm</mat-option>
								</mat-select>
							</mat-form-field>
							<div style="display:flex; justify-content:flex-end;">
								<button mat-flat-button color="primary" (click)="addUser()" [disabled]="adding || !addEmail || !selectedWorldId">
									Add
								</button>
							</div>
						</div>
					</div>

					<div style="padding: 12px 0; border-top: 1px solid rgba(255,255,255,0.08);" *ngIf="usersVm.users.length > 0; else empty">
						<div style="font-weight: 600; margin-bottom: 8px;">Users ({{ usersVm.users.length }})</div>
						<div *ngFor="let u of usersVm.users" style="padding: 12px 0; border-top: 1px solid rgba(255,255,255,0.08);">
							<div style="display:flex; gap: 12px; align-items: center;">
								<div style="min-width:0; flex: 1 1 auto;">
									<div style="font-weight: 600;">{{ u.email || '(no email)' }}</div>
									<div class="muted" style="font-size: 13px;">{{ u.name ?? '—' }} · {{ u.worldRole }}{{ u.isOwner ? ' (owner)' : '' }}</div>
								</div>

								<mat-form-field appearance="fill" style="width: 180px;" *ngIf="!u.isOwner">
									<mat-label>World Role</mat-label>
									<mat-select [value]="u.worldRole" (selectionChange)="setRole(u, $event.value)">
										<mat-option value="member">member</mat-option>
										<mat-option value="gm">gm</mat-option>
									</mat-select>
								</mat-form-field>

								<div class="actions" style="margin:0;">
									<button mat-button color="warn" (click)="remove(u)" [disabled]="u.isOwner">Remove</button>
								</div>
							</div>
						</div>
					</div>

					<ng-template #empty>
						<div class="muted" style="padding: 12px 0; border-top: 1px solid rgba(255,255,255,0.08);">No users found for this world.</div>
					</ng-template>
				</div>
			</mat-card>
		</div>

		<ng-template #loading>
			<div class="page">
				<mat-card class="card">
					<mat-progress-bar mode="indeterminate"></mat-progress-bar>
				</mat-card>
			</div>
		</ng-template>
	`,
})
export class AdminWorldUsersPage {
	private readonly refresh$ = new BehaviorSubject<void>(undefined);

	selectedWorldId: string | null = null;
	addEmail = '';
	addRole: 'member' | 'gm' = 'member';
	adding = false;

	vm$: Observable<{
		worlds: AdminWorldSummary[];
		selectedWorld: AdminWorldSummary | null;
		usersVm: AdminWorldUsersListResponse | null;
	}>;

	constructor(
		private readonly adminWorlds: AdminWorldsService,
		private readonly adminWorldUsers: AdminWorldUsersService
	) {
		const worlds$ = this.refresh$.pipe(switchMap(() => this.adminWorlds.list()), map((r) => r.worlds));
		const selectedWorldId$ = this.refresh$.pipe(startWith(undefined), map(() => this.selectedWorldId));

		this.vm$ = combineLatest([worlds$, selectedWorldId$]).pipe(
			switchMap(([worlds, selectedWorldId]) => {
				const selectedWorld = worlds.find((w) => w.id === selectedWorldId) ?? null;
				if (!selectedWorldId) {
					return [
						{
							worlds,
							selectedWorld,
							usersVm: null,
						},
					];
				}

				return this.adminWorldUsers.list(selectedWorldId).pipe(
					map((usersVm) => ({
						worlds,
						selectedWorld,
						usersVm,
					}))
				);
			}),
			map((x: any) => (Array.isArray(x) ? x[0] : x))
		);
	}

	refresh() {
		this.refresh$.next();
	}

	addUser() {
		if (!this.selectedWorldId) return;
		this.adding = true;
		this.adminWorldUsers.add(this.selectedWorldId, this.addEmail, this.addRole).subscribe({
			next: () => {
				this.adding = false;
				this.addEmail = '';
				this.refresh();
			},
			error: () => {
				this.adding = false;
				this.refresh();
			},
		});
	}

	setRole(user: AdminWorldUsersListItem, role: 'member' | 'gm') {
		if (!this.selectedWorldId) return;
		this.adminWorldUsers.updateRole(this.selectedWorldId, user.userId, role).subscribe({
			next: () => this.refresh(),
			error: () => this.refresh(),
		});
	}

	remove(user: AdminWorldUsersListItem) {
		if (!this.selectedWorldId) return;
		if (user.isOwner) return;
		this.adminWorldUsers.remove(this.selectedWorldId, user.userId).subscribe({
			next: () => this.refresh(),
			error: () => this.refresh(),
		});
	}
}
